import { memo, type ReactNode } from 'react'

import { Group, type GroupProps, Stack, Text, Title } from '@mantine/core'

import { TokenRender } from '@repo/common/components/TokenRender'
import {
  isAddressableToken,
  type ProductSymbol,
  type SimulatedFundSymbol,
  toAddressableToken,
} from '@repo/common/queries/products'

import { OnlyWalletBalance } from '@/components/WalletBalance'

import { ProductAmount } from '../updating_numbers/ProductAmount'

import classes from './InvestmentRow.module.css'

export { classes }

export const InvestmentRow = memo<{
  symbol: ProductSymbol | SimulatedFundSymbol
  name: string
  price_per_token: ReactNode
  nav_per_token?: ReactNode
  one_year_app_type?: 'NAV' | ''
  one_year_apy: ReactNode
  tvl: ReactNode
  children?: ReactNode
  variant?: 'invest' | 'research' | 'details'
  w?: GroupProps['w']
  // eslint-disable-next-line max-lines-per-function, complexity
}>(function InvestmentRow({
  symbol,
  name,
  price_per_token,
  nav_per_token,
  one_year_app_type = 'NAV',
  one_year_apy,
  tvl,
  variant = 'invest',
  children,
  w,
}) {
  return (
    <Group className={classes.root} w={w} align="center">
      <Group w="190px" wrap="nowrap">
        <TokenRender symbol={symbol} size="var(--mantine-font-size-xl2)" />
        <Stack gap="0">
          <Text size="lg" fw="bold" lh="1">
            {name}
          </Text>
          <Text size="sm">{symbol}</Text>
        </Stack>
      </Group>
      <Group gap="sm">
        <Group gap="lg" align="flex-start">
          <Stack gap="0">
            <Title order={2} fz="xs" lh="2">
              Price per Token
            </Title>
            <Text size="lg" lh="md" c="green">
              {price_per_token}
            </Text>
          </Stack>
          {nav_per_token != null && (
            <Stack gap="0">
              <Title order={2} fz="xs" lh="2">
                NAV per Share
              </Title>
              <Text size="lg" lh="md" c="chart_a.5">
                {nav_per_token}
              </Text>
            </Stack>
          )}
          <Stack gap="0">
            <Title order={2} fz="xs" lh="2">
              {one_year_app_type} 1 Year APY
            </Title>
            <Text size="lg" lh="md" c="green">
              {one_year_apy}
            </Text>
          </Stack>
          <Stack gap="0" miw="90px">
            <Title order={2} fz="xs" lh="2">
              TVL
            </Title>
            <Text size="lg" lh="md" c="green">
              {tvl}
            </Text>
          </Stack>
        </Group>
        {['trade', 'details'].includes(variant) && (
          <Group wrap="nowrap" align="flex-start">
            <Stack gap="0" mih="57" miw="100">
              <Title order={2} fz="xs" lh="2">
                My Balance
              </Title>
              <Text size="lg" lh="md">
                <ProductAmount symbol={symbol} />
              </Text>
            </Stack>
            <Stack gap="0" mih="57">
              <Title order={2} fz="xs" lh="2">
                My Tokens
              </Title>
              <Text size="lg" lh="md">
                {isAddressableToken(symbol) ? (
                  <OnlyWalletBalance token={toAddressableToken(symbol)} />
                ) : (
                  '0'
                )}
              </Text>
            </Stack>
          </Group>
        )}
      </Group>
      {children}
    </Group>
  )
})
